import { useRef, useState } from 'preact/hooks'
import { findCoveringHost, normalizeHost } from '@/utils/domainHost'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { NON_PROSPECT_DOMAINS_MAX_COUNT, type NonProspectDomainsViewDto } from '@/types/nonProspectDomains'
import type { ApiResponse } from '@/api/client'

export type SaveNonProspectDomains = (domains: string[], expectedETag?: string) => Promise<ApiResponse<NonProspectDomainsViewDto> | null>
export function domainSaveError(response: { status?: number; error?: string } | null, fallback: string) {
  if (response?.status === 412) return 'This list changed elsewhere. Reload the list and try again. Nothing was saved.'
  return getDisplayErrorMessages(response?.error, fallback).join(' ')
}

/** Shared interaction state only. Each application owns its loading, saving and visual treatment. */
export function useNonProspectDomainsEditor(view: NonProspectDomainsViewDto, onSave: SaveNonProspectDomains) {
  const own = view.NonProspectDomains
  const inherited = view.InheritedDomains
  const [adding, setAdding] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const busy = useRef(false)
  const atLimit = own.length >= NON_PROSPECT_DOMAINS_MAX_COUNT
  const persist = async (domains: string[], etag = view.ETag) => {
    if (busy.current) return false
    busy.current = true
    setSaving(true)
    setError(null)
    try {
      const response = await onSave(domains, etag)
      if (!response?.data || response.error || response.status >= 400) {
        setError(domainSaveError(response, 'The list could not be saved. Try again.'))
        return false
      }
      return true
    } catch (cause) {
      setError(getDisplayErrorMessages(cause, 'The list could not be saved. Try again.').join(' '))
      return false
    } finally { busy.current = false; setSaving(false); setRemoving(null) }
  }
  const handleAdd = async () => {
    if (busy.current) return
    const host = normalizeHost(newDomain)
    if (!host) { setError('Enter a valid domain, such as example.com.'); return }
    const covered = findCoveringHost(host, [...inherited.map(domain => domain.Host), ...own])
    if (covered) { setError(`Already covered by ${covered}. Check the list and its inherited domains.`); return }
    if (atLimit) { setError('You can add at most 1,000 non-prospect domains.'); return }
    if (await persist([...own, host])) setNewDomain('')
  }
  const handleRemove = async (domain: string) => {
    if (busy.current) return
    setRemoving(domain)
    await persist(own.filter(entry => entry !== domain))
  }
  return {
    own, inherited, adding, newDomain, error, saving, removing, atLimit, setNewDomain, handleAdd, handleRemove, persist,
    clearError: () => setError(null),
    openAddForm: () => { setError(null); setAdding(true) },
    closeAddForm: () => { if (!busy.current) { setAdding(false); setNewDomain(''); setError(null) } },
  }
}
export type NonProspectDomainsEditor = ReturnType<typeof useNonProspectDomainsEditor>
